import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Color, LIST_PAGES, Size, pageKindLineText, quickCreateTitle, t, type DealType, type IconMeaning, type StringKey } from '@bms2/shared';
import { Icon } from '../../components/Icon';
import { Button, Dropdown } from '../../components/controls';
import modalStyles from '../../components/modals/modal.module.css';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { AccessDenied } from '../../shell/Fallbacks';
import { PageModule } from '../../shell/PageModule';
import type { ShellOutletContext } from '../../shell/AppShell';
import { NEW_DEAL_TYPE_KEY_PREFIX, readPreference, writePreference } from '../../shell/devicePreference';
import { useViewerZone } from '../../shell/ViewerZone';
import { useItemPicker } from '../settings/api';
import { useApi } from './api';
import { useEmploymentSearch } from './DealModals';
import { PROJECT_PAGE_FIELDS, SHORT_FORM_FIELDS, dealTypeOf, saveDealForm } from './dealForms';
import { PartsDealFields, emptyPartsDeal, type PartsDealDraftState } from './PartsDealFields';
import { RecordFields } from './RecordFields';
import { EMPTY_SERVICE_REQUEST, ServiceRequestFields, type ServiceRequestDraft } from './ServiceRequestFields';
import styles from './NewDealPage.module.css';

/** 🔒 The New Deal page's own address (decision 2510): the Deals list's + New and an account's + New Deal both open it. */
export const NEW_DEAL_ROUTE = `${LIST_PAGES.Deal.route}/new`;

/**
 * 🔒 The probe the page asks on open (piece 18; T1891): the Deal write every
 * form's save asks. A viewer refused it meets Access Denied in place of the
 * form, before anything is typed, rather than at the save.
 */
export const NEW_DEAL_GATE_PATH = '/deals/create-gate';

/** The query parameter naming the account the page was reached from, which it opens with (decisions 0497, 2510). */
export const NEW_DEAL_ACCOUNT_PARAM = 'account';

/** What an account page hands the page beside its address, so the Account reads by name rather than as *Chosen*. */
export interface NewDealArrival {
  accountName?: string;
}

/** The address an account's + New Deal opens: the page, with that account named. */
export function newDealRoute(companyId: string | null = null): string {
  return companyId ? `${NEW_DEAL_ROUTE}?${NEW_DEAL_ACCOUNT_PARAM}=${encodeURIComponent(companyId)}` : NEW_DEAL_ROUTE;
}

/** 🔒 The last type this person picked, remembered in this browser per person (decision 2510); Project where none is. */
export function rememberedDealType(personId: string): DealType {
  return readPreference<DealType>(`${NEW_DEAL_TYPE_KEY_PREFIX}${personId}`, (raw) => dealTypeOf(raw), 'Project');
}

const TILE_NAMES: Readonly<Record<DealType, StringKey>> = {
  Parts: 'deal.new.tile.parts',
  Service: 'deal.new.tile.service',
  Project: 'deal.new.tile.project',
};

const TILE_HINTS: Readonly<Record<DealType, StringKey>> = {
  Parts: 'deal.new.tile.partsHint',
  Service: 'deal.new.tile.serviceHint',
  Project: 'deal.new.tile.projectHint',
};

/** 🔒 Each tile's icon before its name (T1876): a package, a wrench and a hard hat. The name beside it is its label, so the icon carries none. */
const TILE_ICONS: Readonly<Record<DealType, IconMeaning>> = {
  Parts: 'dealParts',
  Service: 'dealService',
  Project: 'dealProject',
};

/** 🔒 The tiles read Parts, Service, Project, the order decision 2510 names them in. */
const TILE_ORDER: readonly DealType[] = ['Parts', 'Service', 'Project'];

/**
 * 🔒 New Deal as a page at its own address (piece 18, *New Deal by type*;
 * decision 2510, amending 2290 and 0497). The type is chosen at the top as three
 * tiles, the form beneath changes with the pick, and the last pick is
 * remembered per person. Reached from an account, the Account is filled in and
 * stays editable. Save creates the Deal and opens its page; Back, like the
 * browser's own back, leaves and writes nothing.
 *
 * 🔨 The forms are the ones the window drew (decisions 2290, 2294): the Parts
 * form, the Service form with its Emergency switch, and the Project form. The
 * window stays for a picker's + Add, where the person returns to the page
 * beneath, and both save through the one by-type save.
 */
export function NewDealPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const navigate = useNavigate();
  const shell = useOutletContext<ShellOutletContext | undefined>();
  const personId = shell?.personId ?? '';
  const [params] = useSearchParams();
  const arrival = (useLocation().state ?? null) as NewDealArrival | null;
  const accountId = params.get(NEW_DEAL_ACCOUNT_PARAM) || null;
  const [values, setValues] = useState<Record<string, unknown>>(() => ({
    dealType: rememberedDealType(personId),
    ...(accountId ? { companyId: accountId, ...(arrival?.accountName ? { companyIdLabel: arrival.accountName } : {}) } : {}),
  }));
  const [parts, setParts] = useState<PartsDealDraftState>(emptyPartsDeal);
  const [request, setRequest] = useState<ServiceRequestDraft>(EMPTY_SERVICE_REQUEST);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** A Deal saved whose confirmation text did not go: the page stays to say so, and its Save is spent so a second press cannot book a second pair (T1781). */
  const [saved, setSaved] = useState<{ id: string; notice: string } | null>(null);
  const picker = useItemPicker(apiBaseUrl, 'sales');
  const zone = useViewerZone();
  const gate = useApi<{ allowed: true }>(NEW_DEAL_GATE_PATH, apiBaseUrl);
  const type = dealTypeOf(values.dealType) ?? 'Project';
  const companyId = typeof values.companyId === 'string' && values.companyId ? values.companyId : null;
  // 🔒 The Project form's Owner (decision 10701; T1009): somebody employed here, the staff scope listing nobody else; nobody picked, the person saving owns it.
  const searchStaff = useEmploymentSearch(apiBaseUrl, null, null, 'staff');
  const ownerName = typeof values.ownerEmploymentIdLabel === 'string' ? values.ownerEmploymentIdLabel : null;

  /**
   * 🔨 The viewer can arrive after the first draw, so the remembered pick is read
   * again once the person is known, until somebody presses a tile.
   */
  const picked = useRef(false);
  useEffect(() => {
    if (!personId || picked.current) return;
    const remembered = rememberedDealType(personId);
    setValues((held) => (held.dealType === remembered ? held : { ...held, dealType: remembered }));
  }, [personId]);

  const pick = (next: DealType) => {
    picked.current = true;
    setValues((held) => ({ ...held, dealType: next }));
    // Until the viewer is known there is no key to remember it under, so nothing is written (T1812).
    if (personId) writePreference(`${NEW_DEAL_TYPE_KEY_PREFIX}${personId}`, next);
  };

  /** 🔒 The tiles are a radio group, so an arrow key moves the pick to the next tile and its focus with it (T1812). */
  const tileRefs = useRef<Partial<Record<DealType, HTMLButtonElement | null>>>({});
  const step = (event: KeyboardEvent<HTMLButtonElement>, from: DealType) => {
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const next = TILE_ORDER[(TILE_ORDER.indexOf(from) + delta + TILE_ORDER.length) % TILE_ORDER.length]!;
    pick(next);
    tileRefs.current[next]?.focus();
  };

  /** 🔒 Back leaves for where the page was reached from and writes nothing (decision 2510). It replaces this page in the history, so the browser's back does not reopen it. */
  const back = () => navigate(accountId ? `${LIST_PAGES.CompanyRecord.route}/${accountId}` : LIST_PAGES.Deal.route, { replace: true });

  const save = async () => {
    setError(null);
    setBusy(true);
    const made = await saveDealForm({ values: { ...values, dealType: type }, parts, request, listPriceOf: (itemId) => picker.prices.current.get(itemId) ?? null, zone, apiBaseUrl });
    setBusy(false);
    if (!made.ok) {
      setError(made.message);
      return;
    }
    // 🔒 An emergency whose text did not go is saved and dispatched; the page says so before it leaves (T1781).
    if (made.data.textNotSent) {
      setSaved({ id: made.data.id, notice: made.data.textNotSent });
      return;
    }
    // 🔒 Save creates the Deal and opens its page (decision 2510), whichever form wrote it.
    navigate(`${LIST_PAGES.Deal.route}/${made.data.id}`, { replace: true });
  };

  const footer = (
    <div className={styles.footer} data-new-deal-footer>
      <Button size={Size.Medium} color={Color.Navy} text={[t('modal.save')]} actions={[() => void save()]} disabled={busy || saved !== null} />
      <span className={styles.footerSpacer} />
      <Button size={Size.Medium} color={Color.Inactive} text={[t('deal.new.back')]} actions={[back]} disabled={busy} />
    </div>
  );

  // 🔒 A viewer the Deal write refuses reads the standard refusal and no form (T1891); a probe that fails to answer leaves the form, and the save still asks.
  if (gate.denied) return <AccessDenied reason={gate.deniedReason} />;

  return (
    <PageModule icon="deal" title={quickCreateTitle(LIST_PAGES.Deal.singular, 'new')} subtitle={pageKindLineText({ page: 'record', recordType: 'Deal' })} footer={footer}>
      <div className={styles.form} data-new-deal-page={type}>
        {saved ? <AlertBanner tone="warning" message={saved.notice} action={{ label: t('deal.new.openSaved'), onAction: () => navigate(`${LIST_PAGES.Deal.route}/${saved.id}`, { replace: true }) }} /> : null}
        {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
        <section className={styles.section} data-new-deal-types>
          <h2 className={styles.heading} id="new-deal-type">{t('deal.new.typeHeading')}</h2>
          <div className={styles.tiles} role="radiogroup" aria-labelledby="new-deal-type">
            {TILE_ORDER.map((kind) => (
              <button
                key={kind}
                type="button"
                role="radio"
                aria-checked={kind === type ? 'true' : 'false'}
                tabIndex={kind === type ? 0 : -1}
                ref={(node) => {
                  tileRefs.current[kind] = node;
                }}
                onKeyDown={(event) => step(event, kind)}
                className={kind === type ? `${styles.tile} ${styles.chosen}` : styles.tile}
                onClick={() => pick(kind)}
                data-new-deal-type={kind}
              >
                <span className={styles.tileTitle} data-new-deal-type-icon={TILE_ICONS[kind]}>
                  <Icon meaning={TILE_ICONS[kind]} size="inline" />
                  <span className={styles.tileName}>{t(TILE_NAMES[kind])}</span>
                </span>
                <span className={styles.tileHint}>{t(TILE_HINTS[kind])}</span>
              </button>
            ))}
          </div>
        </section>
        <section className={styles.section} data-new-deal-form={type}>
          <div className={styles.fields} data-new-record="Deal">
            {/* 🔒 The tiles carry the Type, so the form beneath draws no Type field of its own (decision 2510). */}
            <RecordFields
              recordType="Deal"
              read={(key) => values[key]}
              onChange={(key, value) => setValues((held) => ({ ...held, [key]: value }))}
              apiBaseUrl={apiBaseUrl}
              mode="create"
              only={type === 'Project' ? PROJECT_PAGE_FIELDS : SHORT_FORM_FIELDS.filter((key) => key !== 'dealType')}
            />
            {type === 'Project' ? (
              <span data-new-deal-field="owner">
                <Dropdown size={Size.Medium} label={t('deal.parts.owner')} entityLabel={t('deal.parts.ownerEntity')} search={searchStaff} value={ownerName} onSelect={(row) => setValues((held) => ({ ...held, ownerEmploymentId: row.id, ownerEmploymentIdLabel: row.line1 }))} onAddNew={null} />
                {ownerName ? null : <p className={modalStyles.lead} data-new-deal-owner-hint="true">{t('deal.parts.ownerYou')}</p>}
              </span>
            ) : null}
            {type === 'Parts' ? <PartsDealFields companyId={companyId} draft={parts} onChange={setParts} apiBaseUrl={apiBaseUrl} picker={picker} /> : null}
            {type === 'Service' ? <ServiceRequestFields companyId={companyId} draft={request} onChange={setRequest} apiBaseUrl={apiBaseUrl} /> : null}
          </div>
        </section>
      </div>
    </PageModule>
  );
}
